import { useState, useEffect } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

interface FieldValue {
  value: string;
  status: 'validated' | 'dubious';
  reason?: string;
}

interface DocumentMetadata {
  numero_factura?:  FieldValue;
  fecha_factura?:   FieldValue;
  empresa_emisora?: FieldValue;
  cif_emisor?:      FieldValue;
  base_imponible?:  FieldValue;
  tipo_iva?:        FieldValue;
  iva?:             FieldValue;
  total?:           FieldValue;
  [key: string]: FieldValue | undefined;
}

interface Document {
  id: string;
  nombre: string;
  filename: string;
  mimetype: string;
  ocr_status: string;
  validation_status: 'ai_processed' | 'verified';
  metadata: DocumentMetadata | null;
  created_at: string;
}

interface Props {
  document: Document;
  tenantSlug: string;
  onClose: () => void;
  onOpenExternal?: () => void;
  onSaveField: (docId: string, field: string, value: string) => Promise<void>;
  onVerify: (docId: string) => Promise<void>;
}

// ── Labels ───────────────────────────────────────────────────────────────────

const LABEL_MAP: Record<string, string> = {
  numero_factura:  'N. Factura',
  fecha_factura:   'Fecha',
  empresa_emisora: 'Empresa emisora',
  cif_emisor:      'CIF / NIF emisor',
  base_imponible:  'Base imponible',
  tipo_iva:        'Tipo IVA %',
  iva:             'Importe IVA',
  total:           'Total',
};

const REQUIRED_INVOICE_FIELDS = [
  'empresa_emisora',
  'cif_emisor',
  'numero_factura',
  'fecha_factura',
  'base_imponible',
  'tipo_iva',
  'iva',
  'total',
] as const;

const TIPO_IVA_OPTIONS = ['0', '4', '10', '21'] as const;

// ── Component ────────────────────────────────────────────────────────────────

export default function DocumentVerifyModal({ document: doc, tenantSlug, onClose, onOpenExternal, onSaveField, onVerify }: Props) {
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [pdfScale, setPdfScale] = useState(100);

  // Construir URL del visor de documento
  const token = localStorage.getItem('erp_token') ?? '';
  const viewUrl = `/api/v1/documents/${doc.id}/view?slug=${encodeURIComponent(tenantSlug)}&token=${encodeURIComponent(token)}`;

  const metadata = doc.metadata;
  const isVerified = doc.validation_status === 'verified';

  // Obtener valor de campo (editado o del metadata)
  const getFieldValue = (key: string): string => {
    if (editValues[key] !== undefined) return editValues[key];
    return metadata?.[key]?.value ?? '';
  };

  // Obtener estado del campo
  const getFieldStatus = (key: string): 'validated' | 'dubious' | 'missing' => {
    const val = getFieldValue(key);
    if (!val) return 'missing';
    if (editValues[key] !== undefined) return 'validated';
    return metadata?.[key]?.status ?? 'validated';
  };

  // Guardar campo
  const handleSaveField = async (key: string) => {
    const value = editValues[key];
    if (value === undefined) return;
    
    setSaving(key);
    try {
      await onSaveField(doc.id, key, value);
      // Limpiar editValues para este campo tras guardar
      setEditValues(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setSaving(null);
    }
  };

  // Verificar documento
  const handleVerify = async () => {
    setVerifying(true);
    try {
      await onVerify(doc.id);
      // El padre se encarga de cerrar el modal
    } finally {
      setVerifying(false);
    }
  };

  // Campos obligatorios faltantes
  const missingFields = REQUIRED_INVOICE_FIELDS.filter(key => !getFieldValue(key));
  const canVerify = missingFields.length === 0 && !isVerified;

  // Calcular totales
  const base = parseFloat(getFieldValue('base_imponible').replace(/\./g, '').replace(',', '.')) || 0;
  const iva = parseFloat(getFieldValue('iva').replace(/\./g, '').replace(',', '.')) || 0;
  const total = parseFloat(getFieldValue('total').replace(/\./g, '').replace(',', '.')) || 0;
  const calculatedTotal = base + iva;
  const totalMatches = Math.abs(calculatedTotal - total) < 0.02;

  // Cerrar con ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.8)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          display: 'flex',
          width: '100%',
          maxWidth: 1600,
          height: '100%',
          background: 'var(--color-bg)',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}
      >
        {/* ─── Panel izquierdo: Visor PDF ─── */}
        <div style={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--color-border)' }}>
          {/* Toolbar del visor */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-muted)',
                  cursor: 'pointer',
                  fontSize: 20,
                  padding: 4,
                }}
                title="Cerrar"
              >
                x
              </button>
              <span style={{ color: 'var(--color-text)', fontSize: 14, fontWeight: 500 }}>
                {doc.nombre || doc.filename}
              </span>
            </div>
            
            {/* Controles de zoom */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setPdfScale(s => Math.max(50, s - 25))}
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  padding: '4px 10px',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                -
              </button>
              <span style={{ color: 'var(--color-muted)', fontSize: 13, minWidth: 50, textAlign: 'center' }}>
                {pdfScale}%
              </span>
              <button
                onClick={() => setPdfScale(s => Math.min(200, s + 25))}
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  padding: '4px 10px',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                +
              </button>
              <button
                onClick={() => onOpenExternal ? onOpenExternal() : window.open(viewUrl, '_blank')}
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  padding: '4px 10px',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                  fontSize: 13,
                  marginLeft: 8,
                }}
              >
                Abrir en nueva pestana
              </button>
            </div>
          </div>
          
          {/* Visor PDF */}
          <div style={{ flex: 1, overflow: 'auto', background: '#525659' }}>
            <iframe
              src={viewUrl}
              style={{
                width: `${pdfScale}%`,
                height: `${pdfScale}%`,
                minWidth: '100%',
                minHeight: '100%',
                border: 'none',
                transform: `scale(${pdfScale / 100})`,
                transformOrigin: 'top left',
              }}
              title="Visor de documento"
            />
          </div>
        </div>

        {/* ─── Panel derecho: Formulario ─── */}
        <div style={{ flex: '0 0 420px', display: 'flex', flexDirection: 'column', background: 'var(--color-surface)' }}>
          {/* Header del formulario */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: isVerified ? '#10b981' : missingFields.length > 0 ? '#f59e0b' : '#22c55e',
              }} />
              <span style={{
                fontSize: 13,
                fontWeight: 600,
                color: isVerified ? '#10b981' : missingFields.length > 0 ? '#f59e0b' : '#22c55e',
              }}>
                {isVerified
                  ? 'Documento verificado'
                  : missingFields.length > 0
                    ? `Faltan ${missingFields.length} campo(s)`
                    : 'Listo para verificar'}
              </span>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: 0 }}>
              Revisa los campos antes de confirmar
            </p>
          </div>

          {/* Formulario de campos */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
            {REQUIRED_INVOICE_FIELDS.map(key => {
              const value = getFieldValue(key);
              const status = getFieldStatus(key);
              const isEdited = editValues[key] !== undefined;
              const isSaving = saving === key;
              const reason = metadata?.[key]?.reason;

              return (
                <div key={key} style={{ marginBottom: 16 }}>
                  <label style={{
                    display: 'block',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--color-muted)',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {LABEL_MAP[key] || key}
                    {status === 'missing' && (
                      <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>
                    )}
                  </label>
                  
                  <div style={{ position: 'relative' }}>
                    {key === 'tipo_iva' ? (
                      <select
                        value={value}
                        onChange={e => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
                        disabled={isVerified}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: `1px solid ${
                            status === 'missing' ? '#ef4444' :
                            status === 'dubious' ? '#f59e0b' :
                            isEdited ? '#3b82f6' :
                            'var(--color-border)'
                          }`,
                          background: isVerified ? 'var(--color-bg)' : 'var(--color-surface)',
                          color: 'var(--color-text)',
                          fontSize: 14,
                          cursor: isVerified ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <option value="">Seleccionar...</option>
                        {TIPO_IVA_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}%</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={isEdited ? editValues[key] : value}
                        onChange={e => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
                        disabled={isVerified}
                        placeholder={`Introducir ${LABEL_MAP[key]?.toLowerCase() || key}...`}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          paddingRight: isEdited ? 70 : 12,
                          borderRadius: 8,
                          border: `1px solid ${
                            status === 'missing' ? '#ef4444' :
                            status === 'dubious' ? '#f59e0b' :
                            isEdited ? '#3b82f6' :
                            'var(--color-border)'
                          }`,
                          background: isVerified ? 'var(--color-bg)' : 'var(--color-surface)',
                          color: 'var(--color-text)',
                          fontSize: 14,
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                    
                    {/* Boton guardar si hay cambio */}
                    {isEdited && !isVerified && (
                      <button
                        onClick={() => handleSaveField(key)}
                        disabled={isSaving}
                        style={{
                          position: 'absolute',
                          right: 4,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '4px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: '#3b82f6',
                          color: 'white',
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                          opacity: isSaving ? 0.6 : 1,
                        }}
                      >
                        {isSaving ? '...' : 'Guardar'}
                      </button>
                    )}
                  </div>

                  {/* Mensaje de estado */}
                  {status === 'dubious' && reason && (
                    <p style={{ fontSize: 11, color: '#f59e0b', margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>!</span> {reason}
                    </p>
                  )}
                </div>
              );
            })}

            {/* Resumen de totales */}
            <div style={{
              marginTop: 24,
              padding: 16,
              background: 'var(--color-bg)',
              borderRadius: 8,
              border: '1px solid var(--color-border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>Base imponible</span>
                <span style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>
                  {getFieldValue('base_imponible') || '-'} EUR
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>IVA ({getFieldValue('tipo_iva') || '-'}%)</span>
                <span style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>
                  {getFieldValue('iva') || '-'} EUR
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: 8,
                borderTop: '1px solid var(--color-border)',
              }}>
                <span style={{ fontSize: 14, color: 'var(--color-text)', fontWeight: 600 }}>Total</span>
                <span style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: totalMatches ? '#10b981' : '#f59e0b',
                }}>
                  {getFieldValue('total') || '-'} EUR
                  {!totalMatches && base > 0 && iva > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 4 }}>
                      (calc: {calculatedTotal.toFixed(2)})
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Footer con acciones */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            gap: 12,
          }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--color-text)',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cerrar
            </button>
            <button
              onClick={handleVerify}
              disabled={!canVerify || verifying}
              title={!canVerify ? `Faltan: ${missingFields.map(k => LABEL_MAP[k] || k).join(', ')}` : ''}
              style={{
                flex: 2,
                padding: '12px 16px',
                borderRadius: 8,
                border: 'none',
                background: canVerify ? '#10b981' : 'var(--color-muted)',
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: canVerify ? 'pointer' : 'not-allowed',
                opacity: verifying ? 0.6 : 1,
              }}
            >
              {verifying ? 'Verificando...' : isVerified ? 'Ya verificado' : 'Confirmar datos'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
